import React, { useState, useMemo, useEffect } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Table,
} from "reactstrap"
import { FaPlus, FaPen, FaSearch, FaEye } from "react-icons/fa"
import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa"
import Swal from "sweetalert2"
import moment from "moment"

import SupplierModal from "./SupplierModal"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const Supplier = () => {
  const [suppliers, setSuppliers] = useState([])
  const [supplierData, setSupplierData] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState("") // "add" | "edit" | "view"

  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [loading, setLoading] = useState(false)

  const getAllSuppliers = async () => {
    setLoading(true)
    try {
      const response = await fetchData(setLoading, "supplier")
      if (response?.success) {
        setSuppliers(response.data.reverse() || [])
      } else {
        Swal.fire({
          title: "Error",
          text: response?.message || "Failed to load suppliers",
          icon: "error",
        })
      }
    } catch (err) {
      console.error(err)
      Swal.fire({
        title: "Error",
        text: "Network or server error",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllSuppliers()
  }, [])

  const filteredSuppliers = useMemo(() => {
    if (!searchString.trim()) return suppliers
    const lowerSearch = searchString.toLowerCase()
    return suppliers.filter(
      s =>
        s.supplierName?.toLowerCase().includes(lowerSearch) ||
        s.supplierCode?.toLowerCase().includes(lowerSearch) ||
        s.supplierEmail?.toLowerCase().includes(lowerSearch)
    )
  }, [suppliers, searchString])

  const totalItems = filteredSuppliers.length
  const totalPages = Math.ceil(totalItems / perPageItem)

  const indexOfLastItem = currentPage * perPageItem
  const indexOfFirstItem = indexOfLastItem - perPageItem
  const currentSuppliers = filteredSuppliers.slice(
    indexOfFirstItem,
    indexOfLastItem
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchString, perPageItem])

  const openModal = (type = "", data = {}) => {
    setModalType(type)
    setSupplierData(data)
    setIsModalOpen(true)
  }

  const toggleModal = () => setIsModalOpen(prev => !prev)

  const handleSave = async updatedData => {
    try {
      let response
      if (updatedData.id) {
        // Update
        response = await FetchPutData(
          setLoading,
          "supplier",
          updatedData.id,
          updatedData
        )
        if (response?.data?.success) {
          Swal.fire("Success", "Supplier updated", "success")
        }
      } else {
        // Create
        response = await FetchPostData(setLoading, "supplier", updatedData)
        if (response?.data) {
          Swal.fire("Success", "Supplier created", "success")
        }
      }

      if (response?.data?.success || response?.data) {
        getAllSuppliers()
        toggleModal()
      } else {
        throw new Error(response?.response?.data?.message || "Operation failed")
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Something went wrong",
        icon: "error",
      })
    }
  }

  const toggleStatus = async (id, newStatus) => {
    const result = await Swal.fire({
      title: "Are You Sure?",
      text: `You want to change status of this Supplier to ${
        newStatus ? "Active" : "Inactive"
      }?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3d6eb6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    })

    if (!result.isConfirmed) return // no revert needed if we don't optimistically update yet

    setLoading(true)

    // Optimistic update
    const oldSuppliers = [...suppliers]
    setSuppliers(prev =>
      prev.map(s => (s.id === id ? { ...s, isActive: newStatus } : s))
    )

    try {
      const APIdata = await FetchPutData(setLoading, "supplier", id, {
        isActive: newStatus,
      })

      if (APIdata?.data) {
        Swal.fire({
          title: "Status Updated Successfully.",
          icon: "success",
          timer: 1500,
        })
        // Optional: you can merge real data from response here if needed
      } else {
        setSuppliers(oldSuppliers) // revert
        throw new Error(APIdata?.response?.data?.message || "Failed")
      }
    } catch (err) {
      setSuppliers(oldSuppliers) // revert
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to update status",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className="page-content">
      <Container fluid>
        {loading && <Loader />}

        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Suppliers"
          description="Manage your suppliers"
        />

        <Row>
          <Col lg="12" className="px-0">
            <Card className="mb-0 shadow-lg">
              <CardBody>
                {/* Controls */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="position-relative">
                      <input
                        className="form-control rounded-4 ps-3 pe-5"
                        placeholder="Search by supplier name..."
                        value={searchString}
                        onChange={e => setSearchString(e.target.value)}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>

                    <select
                      className="form-select rounded-4 w-auto"
                      value={perPageItem}
                      onChange={e => setPerPageItem(Number(e.target.value))}
                    >
                      <option value={10}>Show 10</option>
                      <option value={20}>Show 20</option>
                      <option value={50}>Show 50</option>
                    </select>
                  </div>

                  <Button
                    color="primary"
                    className="btn-blue"
                    onClick={() => openModal("add", {})}
                  >
                    <FaPlus className="me-2" /> Supplier
                  </Button>
                </div>

                {/* Table */}
                <Table responsive hover className="align-middle">
                  <thead className="bg-blue text-white">
                    <tr>
                      <th>#</th>
                      <th>Supplier Name</th>
                      <th className="text-center">Supplier Code</th>
                      <th className="text-center">Supplier Email</th>
                      <th>Status</th>
                      <th className="text-center">Added At</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSuppliers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No suppliers found
                        </td>
                      </tr>
                    ) : (
                      currentSuppliers.map((supplier, idx) => (
                        <tr key={supplier.id}>
                          <td>{indexOfFirstItem + idx + 1}</td>
                          <td>{supplier.supplierName || "—"}</td>
                          <td className="text-center">
                            {supplier.supplierCode || "—"}
                          </td>
                          <td className="text-center">
                            {supplier.supplierEmail || "—"}
                          </td>
                          <div className="form-check form-switch d-flex justify-content-center">
                            <Input
                              type="switch"
                              checked={supplier.isActive}
                              onChange={e =>
                                toggleStatus(supplier.id, e.target.checked)
                              }
                            />
                          </div>
                          <td className="text-center">
                            {supplier.createdAt
                              ? moment(supplier.createdAt).format(
                                  "DD-MM-YYYY | hh:mm A"
                                )
                              : "—"}
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              color="info"
                              className="me-2"
                              onClick={() => openModal("view", supplier)}
                            >
                              <FaEye />
                            </Button>
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => openModal("edit", supplier)}
                            >
                              <FaPen />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {totalItems > 0 && (
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                    <div className="text-muted">
                      Showing {currentSuppliers.length} of {totalItems}{" "}
                      suppliers
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        <FaChevronCircleLeft />
                      </Button>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          color={currentPage === i + 1 ? "primary" : "light"}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}

                      <Button
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        <FaChevronCircleRight />
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <SupplierModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        supplierData={supplierData}
        modalType={modalType}
        onSave={handleSave}
      />
    </div>
  )
}

export default Supplier
